import { alpha, ButtonBase, Stack, styled, SxProps, Theme } from '@mui/material';

export const GROUP_COLLAPSE_MS = 200;

export const groupCollapseSx: SxProps<Theme> = {
  // the preview stages groups in a height-capped flex column, which would
  // squeeze this wrapper and leave the rows spilling under the next header
  flexShrink: 0,
  '@media (prefers-reduced-motion: reduce)': { transitionDuration: '0ms' },
};

export const CategoryHeaderButton = styled(ButtonBase, {
  name: 'PolicyCategoryHeader',
  shouldForwardProp: (prop) => prop !== 'open' && prop !== 'isFirst',
})<{ open: boolean; isFirst: boolean }>(({ theme, open, isFirst }) => ({
  display: 'flex',
  alignItems: 'center',
  // ButtonBase centres its children; this header lays them out from the start edge
  justifyContent: 'flex-start',
  gap: '0.375rem',
  width: '100%',
  border: 0,
  cursor: 'pointer',
  backgroundColor: theme.palette.custom.OV.sys.surfaceContainerHighest,
  borderRadius: open ? '0.5rem 0.5rem 0 0' : '0.5rem',
  padding: '0.4375rem 0.5625rem',
  font: 'inherit',
  textAlign: 'left',
  marginTop: isFirst ? 0 : '0.25rem',
  // the corners square up over the same beat the body takes to unfold
  transition: `border-radius ${GROUP_COLLAPSE_MS}ms ease`,

  '&:hover': {
    backgroundColor: alpha(theme.palette.custom.OV.sys.outlineVariant, 0.25),
  },
  '&:focus-visible': {
    outline: `0.125rem solid ${theme.palette.custom.OV.sys.primary}`,
    outlineOffset: '0.0625rem',
  },

  '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
}));

export const CategoryName = styled('span', { name: 'PolicyCategoryName' })(({ theme }) => ({
  flex: 1,
  minWidth: 0,
  fontSize: '0.75rem',
  fontWeight: 600,
  color: theme.palette.custom.OV.sys.onBackground,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
}));

export const CategoryCount = styled('span', { name: 'PolicyCategoryCount' })(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  flexShrink: 0,
  fontSize: '0.6875rem',
  // the badge sets its digits at a smaller size; without a 1 line-height each
  // inherits a different leading and the two numbers sit off each other
  lineHeight: 1,
  color: theme.palette.custom.OV.sys.onSurfaceVariant,
  fontVariantNumeric: 'tabular-nums',
}));

export const RestrictionBadge = styled('span', { name: 'PolicyCategoryRestrictionBadge' })(
  ({ theme }) => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.1875rem',
    flexShrink: 0,
    fontSize: '0.625rem',
    fontWeight: 700,
    borderRadius: '0.3125rem',
    padding: '0.09375rem 0.3125rem',
    fontVariantNumeric: 'tabular-nums',
    backgroundColor: alpha(theme.palette.custom.OV.sys.error, 0.16),
    color: theme.palette.custom.OV.sys.error,
  }),
);

export const categoryChevronSx =
  (open: boolean): SxProps<Theme> =>
  (theme) => ({
    display: 'grid',
    placeItems: 'center',
    flexShrink: 0,
    color: theme.palette.custom.OV.sys.onSurfaceVariant,
    transition: 'transform .15s',
    transform: open ? 'rotate(180deg)' : 'none',
    '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
  });

export const GroupBody = styled(Stack, { name: 'PolicyCategoryGroupBody' })(({ theme }) => ({
  gap: '0.125rem',
  marginBottom: '0.125rem',
  padding: '0.25rem 0.375rem 0.375rem 0.5rem',
  backgroundColor: alpha(theme.palette.custom.OV.sys.surfaceContainerHighest, 0.45),
  borderRadius: '0 0 0.5rem 0.5rem',
}));
